const LOCKED_EXAM_STATUSES = new Set(['', 'NEW', 'NOT_STARTED']);
const LOCKED_CHAPTER_STATUSES = new Set(['', 'NEW', 'NOT_STARTED']);

const toComparableId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
};

const toComparableSerial = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const serial = Number(value);
  return Number.isFinite(serial) ? serial : null;
};

const normalizeValue = (value) => String(value || '').trim().toUpperCase();

const isQuizExam = (exam) =>
  normalizeValue(exam?.subType || exam?.exam?.subType) === 'QUIZ';

const getExamId = (exam) => toComparableId(exam?.primaryId);

const getExamSerial = (exam) =>
  toComparableSerial(exam?.curriculumSerialNumber ?? exam?.exam?.curriculumSerialNumber);

const getEvaluation = (evaluationResult) => (
  Array.isArray(evaluationResult) ? evaluationResult[0] : evaluationResult
);

const getLocalDateTime = (date, timeZone) => date.toLocaleString('en-CA', {
  timeZone,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
}).replace(', ', 'T');

const getOrderedCurriculumItems = (curriculum) => {
  const items = [];

  (curriculum?.chapterList || []).forEach((chapter, originalIndex) => {
    const serial = toComparableSerial(chapter?.curriculumSerialNumber);
    if (serial !== null) {
      items.push({ item: chapter, originalIndex, serial, type: 'CHAPTER' });
    }
  });

  (curriculum?.userExamsList || []).forEach((exam, originalIndex) => {
    const serial = getExamSerial(exam);
    if (!isQuizExam(exam) && serial !== null) {
      items.push({
        item: exam,
        originalIndex: (curriculum?.chapterList || []).length + originalIndex,
        serial,
        type: 'EXAM',
      });
    }
  });

  return items.sort((left, right) =>
    left.serial - right.serial || left.originalIndex - right.originalIndex
  );
};

export const isSelfPacedLearnerFlow = ({ currentCourse, examData }) => {
  const courseProduct = currentCourse?.product;
  const scheduledProduct = examData?.scheduledProduct?.product
    || examData?.scheduledProduct;
  const deliveryType = normalizeValue(
    courseProduct?.deliveryType || scheduledProduct?.deliveryType
  );
  const deliverySubType = normalizeValue(
    courseProduct?.deliverySubType || scheduledProduct?.deliverySubType
  );

  if (deliveryType === 'BRICK_MORTAR' || deliverySubType === 'INSTRUCTOR_LED') {
    return false;
  }

  return deliveryType === 'VIRTUAL'
    && (deliverySubType === 'SELF_PACED' || deliverySubType === '');
};

export const isEvaluatedExam = (evaluationResult) =>
  normalizeValue(getEvaluation(evaluationResult)?.status) === 'EVALUATED';

export const isPassingEvaluation = (evaluationResult) => {
  const evaluation = getEvaluation(evaluationResult);
  return isEvaluatedExam(evaluation)
    && evaluation?.grade?.passFlag === true
    && normalizeValue(evaluation?.grade?.level) !== 'FAIL';
};

/**
 * Returns true when every required (non-Quiz) exam in a learner curriculum has
 * reached the evaluated state. The caller must also validate the current
 * evaluation result as passing; an evaluated result can represent a failure.
 */
export const hasCompletedRequiredExams = (curriculum) => {
  const configuredExams = Array.isArray(curriculum?.userExamsList) && curriculum.userExamsList.length > 0
    ? curriculum.userExamsList
    : curriculum?.examsList || [];
  const requiredExams = configuredExams.filter((exam) => !isQuizExam(exam));

  return requiredExams.length > 0
    && requiredExams.every((exam) => normalizeValue(exam?.status) === 'EVALUATED');
};

export const isCertificateEligibleAfterExam = ({
  certificateEnabled,
  curriculum,
  evaluationResult,
  progressionPassed,
}) => Boolean(
  certificateEnabled
  && progressionPassed
  && isPassingEvaluation(evaluationResult)
  && hasCompletedRequiredExams(curriculum)
);

/**
 * Applies the learner-owned self-paced progression rule to a detached curriculum copy.
 * The caller remains responsible for persisting the returned data.
 */
export const applySelfPacedExamProgression = ({
  userCurriculum,
  completedExamId,
  completedSerial,
  evaluationResult,
  modifiedBy = 'SYSTEM',
  now = new Date(),
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}) => {
  const updatedUserCurriculum = JSON.parse(JSON.stringify(userCurriculum || {}));
  const curriculum = updatedUserCurriculum.curriculum;
  const evaluation = getEvaluation(evaluationResult);

  if (!curriculum || !isEvaluatedExam(evaluation)) {
    return {
      changed: false,
      currentExam: null,
      nextItem: null,
      passed: false,
      userCurriculum: updatedUserCurriculum,
    };
  }

  const orderedItems = getOrderedCurriculumItems(curriculum);
  const comparableCompletedId = toComparableId(completedExamId);
  const comparableCompletedSerial = toComparableSerial(
    completedSerial ?? evaluation?.curriculumSerialNumber
  );
  const currentEntry = orderedItems.find(({ item, type }) =>
    type === 'EXAM'
      && comparableCompletedId !== null
      && getExamId(item) === comparableCompletedId
  ) || orderedItems.find(({ serial, type }) =>
    type === 'EXAM'
      && comparableCompletedSerial !== null
      && serial === comparableCompletedSerial
  );

  if (!currentEntry) {
    return {
      changed: false,
      currentExam: null,
      nextItem: null,
      passed: false,
      userCurriculum: updatedUserCurriculum,
    };
  }

  const effectiveNow = now instanceof Date ? now : new Date(now);
  const modifiedDate = effectiveNow.toISOString();
  const localDateTime = getLocalDateTime(effectiveNow, timeZone);
  const currentExam = currentEntry.item;

  currentExam.status = 'Evaluated';
  currentExam.modifiedBy = modifiedBy;
  currentExam.modifiedDate = modifiedDate;
  currentExam.effectiveDate = {
    ...currentExam.effectiveDate,
    effectiveEndDate: modifiedDate,
    localEndDate: localDateTime,
    timeZone,
  };

  updatedUserCurriculum.modifiedBy = modifiedBy;
  updatedUserCurriculum.modifiedDate = modifiedDate;
  curriculum.modifiedBy = modifiedBy;
  curriculum.modifiedDate = modifiedDate;

  const passed = isPassingEvaluation(evaluation);
  if (!passed) {
    return {
      changed: true,
      currentExam,
      nextItem: null,
      passed: false,
      userCurriculum: updatedUserCurriculum,
    };
  }

  const nextEntry = orderedItems.find(({ serial }) => serial > currentEntry.serial) || null;
  if (!nextEntry) {
    curriculum.status = 'COMPLETED';
    return {
      changed: true,
      currentExam,
      nextItem: null,
      passed: true,
      userCurriculum: updatedUserCurriculum,
    };
  }

  if (curriculum.status !== 'COMPLETED') {
    curriculum.status = 'IN_PROGRESS';
  }

  if (nextEntry.type === 'EXAM') {
    if (LOCKED_EXAM_STATUSES.has(normalizeValue(nextEntry.item.status))) {
      nextEntry.item.status = 'ReadyToTake';
      nextEntry.item.modifiedBy = modifiedBy;
      nextEntry.item.modifiedDate = modifiedDate;
      nextEntry.item.effectiveDate = {
        ...nextEntry.item.effectiveDate,
        effectiveStartDate: modifiedDate,
        localStartDate: localDateTime,
        timeZone,
      };
    }
  } else if (LOCKED_CHAPTER_STATUSES.has(normalizeValue(nextEntry.item.status))) {
    nextEntry.item.status = 'IN_PROGRESS';
    nextEntry.item.modifiedBy = modifiedBy;
    nextEntry.item.modifiedDate = modifiedDate;
  }

  return {
    changed: true,
    currentExam,
    nextItem: { item: nextEntry.item, type: nextEntry.type },
    passed: true,
    userCurriculum: updatedUserCurriculum,
  };
};

export const buildReadyToTakeExamPayload = ({
  academyExam,
  modifiedBy,
  now = new Date(),
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}) => {
  if (!academyExam || !LOCKED_EXAM_STATUSES.has(normalizeValue(academyExam.status))) {
    return null;
  }

  const effectiveNow = now instanceof Date ? now : new Date(now);
  const modifiedDate = effectiveNow.toISOString();

  return {
    ...academyExam,
    status: 'ReadyToTake',
    modifiedBy,
    modifiedDate,
    effectiveDate: {
      ...academyExam.effectiveDate,
      effectiveStartDate: modifiedDate,
      localStartDate: getLocalDateTime(effectiveNow, timeZone),
      timeZone,
    },
  };
};
